import type { IModelsAPI } from '@wix/thunderbolt-symbols'
import type { BootstrapData, CoreSdkLoaders } from '../../types'
import type { IViewerHandlers, IComponentSdksManager, IControllersExports } from '../types'
import { DocumentSdkFactory } from '../componentsSDK/Document'
import { logSdkError, logSdkWarning } from '@wix/thunderbolt-commons'
import {
	APPLICATIONS,
	BOOTSTRAP_DATA,
	COMPONENT_SDK_STATE,
	COMPONENT_SDKS_MANAGER,
	CONTROLLER_EVENTS,
	CONTROLLERS_EXPORTS,
	CORE_SDKS_LOADERS,
	INSTANCE_CACHE,
	MODELS_API,
	VIEWER_HANDLERS,
	WIX_SELECTOR,
} from './moduleNames'
import type { IWixSelector } from './wixSelector'
import type { IInstanceCache } from './instanceCache'
import type { IControllerEvents } from './controllerEvents'
import type { IComponentSdkState } from './componentSdkState'
import type { IApplications } from './applications'

const coreSdkLoaders = (
	wixSelector: IWixSelector,
	modelsApi: IModelsAPI,
	bootstrapData: BootstrapData,
	controllersExports: IControllersExports,
	{ viewerHandlers: handlers }: IViewerHandlers,
	sdkInstancesCache: IInstanceCache,
	controllerEvents: IControllerEvents,
	componentSdkState: IComponentSdkState,
	applications: IApplications,
	componentSdksManager: IComponentSdksManager
) => {
	const reporter = {
		logSdkError,
		logSdkWarning,
	}

	const AppControllerSdkLoader = async () => {
		const { AppControllerSdk } = await import('../componentsSDK/AppController' /* webpackChunkName: "AppController.corvid" */)
		return AppControllerSdk({ controllersExports, modelsApi, controllerEvents })
	}

	const AppWidgetSdkLoader = async () => {
		const { AppControllerWithChildrenSdk } = await import('../componentsSDK/AppController' /* webpackChunkName: "AppController.corvid" */)
		return AppControllerWithChildrenSdk({ controllersExports, modelsApi, controllerEvents })
	}

	const RepeaterSdkLoader = async () => {
		const { RepeaterSdk } = await import('../componentsSDK/repeaters/Repeater' /* webpackChunkName: "Repeater.corvid" */)
		return RepeaterSdk({
			modelsApi,
			wixSelector,
			reporter,
			sdkInstancesCache,
			componentSdkState,
			createRepeatedControllers: applications.createRepeatedControllers,
			handlers,
			controllerEvents,
		})
	}

	const TemplateContainerSdkLoader = async () => {
		const { TemplateContainerSdk } = await import('../componentsSDK/repeaters/TemplateContainer' /* webpackChunkName: "TemplateContainer.corvid" */)
		return TemplateContainerSdk({
			modelsApi,
			wixSelector,
			reporter,
			sdkInstancesCache,
			componentSdkState,
			createRepeatedControllers: applications.createRepeatedControllers,
			handlers,
			controllerEvents,
		})
	}

	const DocumentSdkLoader = async () =>
		Promise.resolve(
			DocumentSdkFactory({
				modelsApi,
				wixSelector,
				currentPageId: bootstrapData.currentPageId,
			})
		)

	const coreSdks: CoreSdkLoaders = {
		AppController: AppControllerSdkLoader,
		AppWidget: AppWidgetSdkLoader,
		TPAWidget: AppControllerSdkLoader,
		TPASection: AppControllerSdkLoader,
		TPAMultiSection: AppControllerSdkLoader,
		TPAGluedWidget: AppControllerSdkLoader,
		tpaWidgetNative: AppControllerSdkLoader,
		Repeater: RepeaterSdkLoader,
		TemplateContainer: TemplateContainerSdkLoader,
		Document: DocumentSdkLoader,
		RefComponent: async () => () => ({}), // RefComponents do not have a connectionQuery, but they're still accessible via getChildren api.
	}

	return {
		init() {
			componentSdksManager.fetchComponentsSdks(coreSdks)
		},
	}
}

export default {
	factory: coreSdkLoaders,
	deps: [WIX_SELECTOR, MODELS_API, BOOTSTRAP_DATA, CONTROLLERS_EXPORTS, VIEWER_HANDLERS, INSTANCE_CACHE, CONTROLLER_EVENTS, COMPONENT_SDK_STATE, APPLICATIONS, COMPONENT_SDKS_MANAGER],
	name: CORE_SDKS_LOADERS,
}
